import { useIntl } from 'react-intl';

import { getPaymentMethodLogo } from '@/components/ShoppingBag/utils';
import { Typography } from '@/components/Typography';
import type { PaymentMethodName } from '@/lib/order';
import { getFriendlyPaymentName } from '@/lib/order';
import type { PaymentRecord } from '@/lib/payment/types';
import { PAYMENT_TYPES } from '@/lib/payment/types';
import { commonMessages } from '@/messages/common';
import { getPaymentCompletedMsg, PaidWith } from '@/messages/payment';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './PaymentRecords.styles';

interface PaymentRecordsProps {
  className?: string;
  currency?: string;
  depositPercentage?: number;
  hasDepositRecord?: boolean;
  isOfflinePayment?: string | null;
  paymentRecords?: (PaymentRecord | null | undefined)[];
  showCheckIcon: boolean;
  showOrder: boolean;
  totalAmount?: number;
}
export const PaymentRecords = ({
  className,
  currency,
  depositPercentage,
  hasDepositRecord,
  isOfflinePayment,
  paymentRecords,
  showCheckIcon,
  showOrder,
  totalAmount,
}: PaymentRecordsProps) => {
  const { formatPriceString } = useRegionProvider();
  const { formatMessage } = useIntl();
  const displayPaymentRecords =
    (!paymentRecords || paymentRecords?.length === 0) &&
    isOfflinePayment?.toLowerCase() === 'true'
      ? [
          {
            id: 'offline_payment',
            name: 'BANK TRANSFER',
            type: PAYMENT_TYPES.OFFLINE,
            amount: totalAmount,
          },
        ]
      : paymentRecords;
  const getOrderLabel = (index: number) => {
    if (!hasDepositRecord) {
      return formatMessage(getPaymentCompletedMsg(index + 1));
    }

    if (index > 0) {
      return formatMessage(getPaymentCompletedMsg(index));
    }

    const depositLabel = formatMessage(commonMessages.firstDepositPayment);

    return depositPercentage === undefined
      ? depositLabel
      : `${depositLabel} ${depositPercentage}%`;
  };

  return (
    <S.Wrapper className={className}>
      {displayPaymentRecords?.map((payment, index) => (
        <S.Item key={payment?.id}>
          <S.ImageWrapper $showCheckIcon={showCheckIcon}>
            {getPaymentMethodLogo(
              payment?.name as PaymentMethodName,
              payment?.type,
            )}
            {showCheckIcon && <S.CheckIcon />}
          </S.ImageWrapper>
          <S.MessageWrapper $showCheckIcon={showCheckIcon}>
            {showOrder && (
              <Typography scale='p2'>{getOrderLabel(index)}</Typography>
            )}
            <Typography
              color='navyBlueSub1'
              fontFamily='italianPlate'
              scale='h5'
            >
              {getFriendlyPaymentName(payment?.name ?? '', payment?.type)
                ? PaidWith(
                    formatPriceString({
                      amount: payment?.amount,
                      currency,
                    }),
                    getFriendlyPaymentName(payment?.name ?? '', payment?.type),
                  )
                : formatPriceString({
                    amount: payment?.amount,
                    currency,
                  })}
            </Typography>
          </S.MessageWrapper>
        </S.Item>
      ))}
    </S.Wrapper>
  );
};
